import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Divider
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';
import { formatBytes, formatDate } from '../utils/formatters';

const OSImagesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [osImages, setOsImages] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  const fetchOSImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getOSImages();
      setOsImages(response.data.images);
    } catch (err) {
      setError('OS 이미지 목록을 불러오는 중 오류가 발생했습니다.');
      enqueueSnackbar('OS 이미지 목록을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOSImages();
  }, []);

  const handleRefresh = () => {
    fetchOSImages();
  };

  const handleUploadOpen = () => {
    setUploadOpen(true);
  };

  const handleUploadClose = () => {
    setUploadOpen(false);
    setSelectedFile(null);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar('업로드할 파일을 선택해주세요.', { variant: 'warning' });
      return;
    }

    setUploading(true);
    try {
      await apiService.uploadOSImage(selectedFile);
      enqueueSnackbar('OS 이미지가 성공적으로 업로드되었습니다.', { variant: 'success' });
      handleUploadClose();
      fetchOSImages();
    } catch (err) {
      enqueueSnackbar('OS 이미지 업로드 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (image) => {
    setImageToDelete(image);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setImageToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;
    
    try {
      await apiService.deleteOSImage(imageToDelete.name);
      enqueueSnackbar(`OS 이미지 ${imageToDelete.name}이 삭제되었습니다.`, { variant: 'success' });
      fetchOSImages();
    } catch (err) {
      enqueueSnackbar(
        `OS 이미지 삭제 중 오류가 발생했습니다: ${err.response?.data?.detail || err.message}`, 
        { variant: 'error' }
      );
    } finally {
      handleDeleteConfirmClose();
    }
  };

  if (loading && osImages.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">OS 이미지 관리</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleUploadOpen}
          >
            이미지 업로드
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            새로고침
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>이미지명</TableCell>
                <TableCell>경로</TableCell>
                <TableCell>크기</TableCell>
                <TableCell>생성일</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {osImages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      등록된 OS 이미지가 없습니다.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                osImages.map((image) => (
                  <TableRow key={image.name} hover>
                    <TableCell>{image.name}</TableCell>
                    <TableCell>{image.path}</TableCell>
                    <TableCell>{formatBytes(image.size)}</TableCell>
                    <TableCell>{formatDate(image.created)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="이미지 삭제">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(image)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 업로드 다이얼로그 */}
      <Dialog open={uploadOpen} onClose={handleUploadClose}>
        <DialogTitle>OS 이미지 업로드</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 400 }}>
            <Typography variant="body1" paragraph>
              업로드할 OS 이미지 파일을 선택해주세요. (.qcow2, .img, .iso 파일)
            </Typography>
            <input
              type="file"
              accept=".qcow2,.img,.iso"
              onChange={handleFileChange}
              style={{ marginTop: '1rem' }}
            />
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                선택된 파일: {selectedFile.name} ({formatBytes(selectedFile.size)})
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadClose} color="inherit" disabled={uploading}>
            취소
          </Button>
          <Button
            onClick={handleUpload}
            color="primary"
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? '업로드 중...' : '업로드'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle>OS 이미지 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {`${imageToDelete?.name} OS 이미지를 정말로 삭제하시겠습니까?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose} color="inherit">
            취소
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OSImagesPage; 